/**
 * userPermissions.ts — a UI-shaping adapter over `@communicator/shared`'s
 * permissions rules, for the Users page (docs/superpowers/specs/
 * 2026-08-09-users-and-permissions-design.md §6): "the UI only avoids
 * offering what the server would refuse."
 *
 * A CONVENIENCE, NEVER THE BOUNDARY. `packages/shared/src/permissions.ts`'s
 * own header comment says it plainly: it is the ONE definition of these
 * judgments. This file is not a second opinion on what is allowed — it calls
 * the exact same `canChangeRole`/`canChangeAdmin`/`canChangeActive` functions
 * `services/ops/src/routes/users.ts` enforces with, and only reshapes the
 * result for a control's disabled/enabled state (dropping `code`, which this
 * page has never needed — see `UiPermissionResult` below). The real
 * enforcement is still the server re-deriving the same verdict, fresh, from
 * the actor/target rows on every `PATCH /ops/users/:id`. `UsersSection.tsx`
 * never trusts this file's `ok: true` as permission to skip the network
 * call, and a refusal from the real request always reverts the row to the
 * server's last-known state (see that file) — exactly the same discipline
 * `CoverageToggle` already established for self-service writes, just for
 * someone else's row instead of your own.
 *
 * USED TO BE A HAND PORT, KEPT DRIFT-PRONE ON PURPOSE UNTIL NOW. Before
 * `packages/shared` grew a permissions module, this file's rules were
 * transcribed by hand from `services/ops/src/permissions.ts` (since deleted —
 * its rules moved into `packages/shared/src/permissions.ts` wholesale, not
 * merely re-exported, so there is nothing left at that old path at all) and
 * flagged in this same comment as the one place in that task most likely to
 * go stale. That is no longer true: there is nothing left to transcribe,
 * because there is nothing left here but a call and a reshape.
 *
 * NOT CALLED HERE: `canListUsers`. `UsersSection.tsx` (moved 2026-08-10 into
 * Settings; used to be a standalone page behind a route-level
 * `RequireAdmin`) checks `store.admin` itself and returns before fetching
 * the list or constructing an actor at all when it is false, so every
 * `actor` this file ever sees already has `admin: true` — the question "can
 * this actor see the list at all" is moot by the time any of these
 * functions run.
 */
import {
  canChangeActive as sharedCanChangeActive,
  canChangeExtension as sharedCanChangeExtension,
  canChangeHaloAgentName as sharedCanChangeHaloAgentName,
  canChangeAdmin as sharedCanChangeAdmin,
  canChangeRole as sharedCanChangeRole,
} from '@communicator/shared'
import type { PermActor, PermTarget, PermissionResult } from '@communicator/shared'

export type UiActor = PermActor
export type UiTarget = PermTarget

export interface UiPermissionResult {
  ok: boolean
  /** Present exactly when `ok` is false — the reason a disabled control shows, per design §6. */
  reason?: string
}

/**
 * Drops `code` from the shared result. The server turns `code` into a wire
 * error field (`errorBody(check.code, check.reason)` in routes/users.ts) for
 * a caller that might branch on it programmatically; this page only ever
 * shows `reason` to a human next to a disabled control, so keeping `code`
 * here would be a field nothing reads.
 */
function toUi(result: PermissionResult): UiPermissionResult {
  return result.ok ? { ok: true } : { ok: false, reason: result.reason }
}

/** UI-facing wrapper around the shared `canChangeRole` rule (table row 2). */
export function canChangeRole(actor: UiActor, target: UiTarget): UiPermissionResult {
  return toUi(sharedCanChangeRole(actor, target))
}

/** UI-facing wrapper around the shared `canChangeAdmin` rule (table row 3). */
export function canChangeAdmin(actor: UiActor, target: UiTarget): UiPermissionResult {
  return toUi(sharedCanChangeAdmin(actor, target))
}

/**
 * UI-facing wrapper around the shared `canChangeActive` rule (table row 4).
 * `requestedActive` matters — see the shared function's own comment — so a
 * caller here must pass the value the toggle is actually about to request,
 * not merely the row's current state.
 */
export function canChangeActive(actor: UiActor, target: UiTarget, requestedActive: boolean): UiPermissionResult {
  return toUi(sharedCanChangeActive(actor, target, requestedActive))
}

/**
 * UI-facing wrapper around the shared `canChangeExtension` rule. Takes no
 * target, because the answer does not depend on one: an extension is a fact
 * about where a person's phone is, not authority they hold, so any admin may
 * set anyone's — the SuperAdmin's included.
 */
export function canChangeExtension(actor: UiActor): UiPermissionResult {
  return toUi(sharedCanChangeExtension(actor))
}

/**
 * UI-facing wrapper around the shared `canChangeHaloAgentName` rule. Target-free
 * for the same reason as the extension above: it records which Halo agent a
 * person IS, not authority they hold, so any admin may set anyone's.
 */
export function canChangeHaloAgentName(actor: UiActor): UiPermissionResult {
  return toUi(sharedCanChangeHaloAgentName(actor))
}
